/**
 * useAutoGenerateTermSheet.ts
 *
 * Extracted hook containing ONLY the logic required for the "autogenerate"
 * termSheetAction flow.
 *
 * Entry point: getTermSheetDataBasedOnAction()
 *   └─ (autogenerate branch)
 *       ├─ getWaivers()
 *       ├─ If latestTermSheetVersion exists  → fetchGetTermSheetBasedOnVersion()
 *       │     └─ getTermSheetData(), getSuggestedLoanTerms()
 *       │     └─ builds newTermSheetData
 *       └─ If NO latestTermSheetVersion      → getTermSheetData(), getSuggestedLoanTerms(), getFeeDetails()
 *             └─ builds newTermSheetData
 *       └─ saveOrUpdateNewTermSheet(newTermSheetData, "create", true)  [always "create" + emailSent=true]
 *             └─ generateOrUpdateTermSheet(..., "create")
 *                   └─ getGenarateTermSheetPayload()
 *                         └─ getLoanData()
 *                   └─ generateNewTermSheet()   (API call)
 */

import { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import jwt_decode from "jwt-decode";
import { LoansContext } from "../context/LoansContext";
import { RootContext } from "../context/RootContext";
import { ParamType } from "../pages/Loans/v2/LoansPagev2";
import {
  formatValueByType,
  getCookie,
  sanitizeCurrency,
  sanitizePercentage,
} from "../utils/Common";
import { ObjectType } from "../types/tableTypes";
import { fetchRequestWaiver } from "../services/network";
import {
  downloadTermsheetPdf,
  generateNewTermSheet,
  getFieldConfigBasedOnOriginator,
  getTermSheetBasedOnVersion,
  sendTermSheetEmail,
} from "../services/termSheet";
import { normalizeFeeDetailsConfig } from "../utils/Constants";
import {
  formatDateForPDFName,
  getSuggestedLoanTerms,
  getTermSheetData,
} from "../components/termSheet/Common";

// ─── context / helpers (unchanged imports from original file) ──────────────────

// ─── props ─────────────────────────────────────────────────────────────────────
interface UseAutoGenerateTermSheetProps {
  evaluationID?: string | number;
}

// ══════════════════════════════════════════════════════════════════════════════
export function useAutoGenerateTermSheet({
  evaluationID = "",
}: UseAutoGenerateTermSheetProps) {
  // ── contexts ──────────────────────────────────────────────────────────────
  const loanContextData = useContext(LoansContext);
  const {
    dynamicConfig,
    termSheetData,
    setTermSheetData,
    setTermSheetEmailData,
    setTermSheetLoanData,
    setTermSheetSuggestedLoanData,
    setTermSheetFeeData,
    setTermSheetRequirementsData,
    termSheetAction, // must equal "autogenerate" to enter this flow
    setTermSheetAction,
    setIsOpenTermSheet,
    setSnackBarMessage,
    userRole,
    setOriginatorAccountMappingData,
    currentTermSheetVersion,
    setCurrentTermSheetVersion,
  } = useContext(RootContext);

  const { t } = useTranslation();
  const params = useParams<ParamType>();
  const { loanType, loanId } = params;

  const { latestTermSheetVersion, setLatestTermSheetVersion } = loanContextData;

  // ── local state ───────────────────────────────────────────────────────────
  const [orgLevelFees, setOrgLevelFees] = useState<any>([]);
  const [defaultFeeDetails, setDefaultFeeDetails] = useState<any>([]);
  const [orgLevelRequirements, setOrgLevelRequirements] = useState<any>([]);
  const [originatorLogo, setOriginatorLogo] = useState<string>("");
  const [latestPDF_URL, setLatestPDF_URL] = useState<string>("");
  const [signatureData, setSignatureData] = useState<any>({});
  const [isMultiProperty, setIsMultiProperty] = useState<boolean>(false);
  const [waiverRequest, setWaiverRequest] = useState<any>("");
  const [
    originatorsAccountMappingDetails,
    setOriginatorsAccountMappingDetails,
  ] = useState<any>({});

  // ── derived / memoised ────────────────────────────────────────────────────
  let originatorData: any =
    getCookie("originatorData") ||
    localStorage.getItem("originatorData") ||
    "{}";
  originatorData = JSON.parse(originatorData);

  const orgId =
    loanContextData?.fetchData?.orgId ||
    loanContextData?.loanDetails?.orgId ||
    getCookie("orgId");

  const accessToken: string = getCookie("sizer_access_token") || "";
  const decodedData: ObjectType = jwt_decode(accessToken);

  /**
   * Default email body – only used when no saved emailBody exists for the
   * current term-sheet version.
   */
  const emailBodyStr2 = useMemo(
    () => `<p>
      Dear (--- please enter name here ---),
    </p>
    <p>
      Thank you for the opportunity to provide financing on this property.
      ${originatorData?.accountName} would consider structuring a loan subject
      to the following outline.
    </p>`,
    [originatorData?.accountName],
  );

  // ══════════════════════════════════════════════════════════════════════════
  // 1. WAIVERS
  // ══════════════════════════════════════════════════════════════════════════
  /** Fetches waiver data for the current loan and stores it locally. */
  async function getWaivers() {
    try {
      const result = await fetchRequestWaiver([loanId]);
      setWaiverRequest(result);
      return result;
    } catch (err) {
      console.error(err);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 2. ORG-LEVEL FEE CONFIG  (called once on mount via useEffect below)
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Loads originator-level fee configuration, requirements, logo, and SMTP
   * settings.  Must complete before getTermSheetDataBasedOnAction so that
   * defaultFeeDetails / orgLevelFees are populated.
   */
  const setFeeDetailsConfig = async () => {
    const headers = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
    try {
      const { data } = await getFieldConfigBasedOnOriginator(orgId, headers);
      setOriginatorAccountMappingData({ ...data });
      setOriginatorLogo(data?.originatorLogo);
      setOriginatorsAccountMappingDetails({
        fromEmail: data?.fromEmail,
        smtpIp: data?.smtpIp,
        passKey: data?.passKey,
      });

      const defaultRequirements = data?.requirement?.map(
        (req: any) => req?.data,
      );
      if (defaultRequirements?.length > 0) {
        setOrgLevelRequirements([...defaultRequirements]);
      }

      const config: any = normalizeFeeDetailsConfig(data, loanType, userRole);
      setOrgLevelFees(config);
      setDefaultFeeDetails(
        config?.filter((field: any) => field?.isVisible(userRole)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 3. HELPERS  (used inside the autogenerate pipeline)
  // ══════════════════════════════════════════════════════════════════════════

  /** Strip "$" and "," so raw numbers reach the backend. */
  const getLoanData = (loanData: any) => {
    const result: any = {};
    Object.keys(loanData).forEach((key) => {
      const value =
        typeof loanData[key] === "string" &&
        (loanData[key]?.includes(",") || loanData[key]?.includes("$"))
          ? loanData[key]?.replace(/[$,]/g, "")
          : loanData[key];
      result[key] = value;
    });
    return result;
  };

  /** Produces a human-readable timestamp name for a new term-sheet version. */
  const getTermsheetDateTimeStampName = () => {
    const date = new Date();
    return (
      `${t("termSheetKeyForHeader")} v ` +
      `${(date.getMonth() + 1).toString().padStart(2, "0")}-` +
      `${date.getDate().toString().padStart(2, "0")}-` +
      `${date.getFullYear()},` +
      `${date.getHours().toString().padStart(2, "0")}:` +
      `${date.getMinutes().toString().padStart(2, "0")}`
    );
  };

  /** Returns the initial fee-values object derived from org-level defaults. */
  const getFeeDetails = () => {
    const feeDetailsObj: any = {};
    defaultFeeDetails?.forEach((obj: any) => {
      if (obj?.isVisible(userRole)) {
        const field = orgLevelFees?.find(
          (fee: any) => fee?.label === obj?.label,
        );
        let formattedValue: string;
        if (field?.defaultValue) {
          formattedValue =
            obj?.label === "Origination Points"
              ? sanitizePercentage(field?.defaultValue)
              : sanitizeCurrency(field?.defaultValue);
        } else {
          formattedValue = "$0.00";
        }
        feeDetailsObj[obj?.label] = formattedValue;
      }
    });
    return { ...feeDetailsObj };
  };

  /**
   * Removes duplicate city/state/zip fragments from a property location object
   * and returns a clean address string.
   */
  const cleanedAddressString = (propertyLocation: any) => {
    if (!propertyLocation) return "";
    const pattern = new RegExp(
      `\\b(${propertyLocation.postalCode}|${propertyLocation.city}|${propertyLocation.state})\\b`,
      "g",
    );
    const cleanedAddress = propertyLocation.address
      ?.replace(pattern, "")
      ?.trim()
      ?.replace(/\s+/g, " ");
    return propertyLocation?.address
      ? `${cleanedAddress}, ${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`
      : `${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 4. FETCH A SPECIFIC TERM-SHEET VERSION
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Thin wrapper around getTermSheetBasedOnVersion.
   * Used in the autogenerate branch when latestTermSheetVersion already exists.
   */
  const fetchGetTermSheetBasedOnVersion = async (
    termSheetVersion = currentTermSheetVersion,
  ) => {
    try {
      const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
      const response = await getTermSheetBasedOnVersion(
        realmHeaders,
        loanId,
        +termSheetVersion,
      );
      return response?.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 5. BUILD THE CREATE PAYLOAD
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Assembles the full payload expected by generateNewTermSheet.
   * Mirrors the original getGenarateTermSheetPayload but scoped to autogenerate.
   */
  const buildAutoGeneratePayload = (
    loanData: any,
    defaultFees: any,
    tempTermSheetData: any,
    suggestedTerms: any,
  ) => {
    // Flatten the nested loanData arrays into one merged object
    const termSheetInfo = [
      ...loanData.flat(),
      ...(suggestedTerms?.length > 0 ? suggestedTerms.flat() : []),
    ].reduce((acc: any, obj: any) => ({ ...acc, ...obj }), {});

    // Convert propertyAddressDetails array → plain object
    const newPropertyAddressDetails: any = {};
    tempTermSheetData?.propertyAddressDetails?.map((item: any) => {
      newPropertyAddressDetails[item?.label] = item?.value;
    });

    return {
      termSheetLoanInfo: { ...getLoanData(termSheetInfo) },
      signatureInfo: { ...signatureData },
      feeDetails: defaultFees,
      requirements: tempTermSheetData?.requirements?.filter(
        (r: string) => r !== "",
      ),
      emailSentOn: loanData.emailSentOn,
      isAutoGenerated: true, // always true for autogenerate
      termSheetName:
        tempTermSheetData?.termSheetName ?? getTermsheetDateTimeStampName(),
      propertyAddressDetails: newPropertyAddressDetails,
      email: {
        from: termSheetData?.emails?.fromEmail ?? "noreply@bplsizer.com",
        to: tempTermSheetData?.emails?.toEmails ?? [],
        cc: tempTermSheetData?.emails?.ccEmails ?? [],
        subject: tempTermSheetData?.emails?.subject,
        content: tempTermSheetData?.emailBody ?? emailBodyStr2,
        date: moment().format("YYYY-MM-DD"),
      },
    };
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 6. BUILD EMAIL / PDF PAYLOAD  (needed by sendAutoGenerateEmail)
  // ══════════════════════════════════════════════════════════════════════════
  const buildEmailPayload = (tempTermSheetData: any) => {
    const loanDataFields: any[] = [];
    const suggestedDataFields: any[] = [];

    tempTermSheetData?.loanData?.forEach((loanInfo: any) => {
      Object.keys(loanInfo?.[0] ?? {}).forEach((key: any) =>
        loanDataFields.push({ label: key, value: loanInfo?.[0]?.[key] }),
      );
    });

    tempTermSheetData?.suggestedLoanTerms?.forEach((loanInfo: any) => {
      Object.keys(loanInfo?.[0] ?? {}).forEach((key: any) =>
        suggestedDataFields.push({ label: key, value: loanInfo?.[0]?.[key] }),
      );
    });

    const feeDetailsFormat = Object.keys(
      tempTermSheetData?.feeDetails ?? {},
    ).map((fee: any) => {
      const feeConfig = orgLevelFees?.find((el: any) => el?.label === fee);
      const dataType =
        feeConfig?.dataType === "percentage"
          ? "percentageWithout100x"
          : feeConfig?.dataType;
      return {
        label: fee,
        value: formatValueByType(
          tempTermSheetData?.feeDetails?.[fee],
          dataType,
        ),
      };
    });

    return {
      templateData: {
        subject: tempTermSheetData?.emails?.subject,
        originatorLogo,
        emailBody: tempTermSheetData?.emailBody ?? emailBodyStr2,
        loanType: loanType === "InvestorDSCR" ? "dscr" : "bridge",
        totalPoints:
          loanContextData?.loanDetails?.loanEconomics
            ?.totalOrgAndDiscountPoints ?? null,
        loanDetails: { dynamicFields: loanDataFields },
        suggestedLoanTerms: { dynamicFields: suggestedDataFields },
        propertyAddressDetails: tempTermSheetData?.propertyAddressDetails,
        feeDetails: { dynamicFields: feeDetailsFormat },
        requirements:
          tempTermSheetData?.requirements?.filter((r: string) => r !== "") ??
          [],
        viewLoan: window.location.href,
        originator: {
          fullName: signatureData?.name || signatureData?.accountName,
          email: signatureData?.email || "",
          originatorName: signatureData?.accountName || "",
          phone: signatureData?.phone || "",
        },
        termSheetName: tempTermSheetData?.termSheetName,
      },
      sendTo: tempTermSheetData?.emails?.toEmails ?? [],
      sendCc: tempTermSheetData?.emails?.ccEmails ?? [],
      sender:
        originatorsAccountMappingDetails?.fromEmail ?? "noreply@bplsizer.com",
    };
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 7. CREATE TERM-SHEET IN BACKEND  (always "create" for autogenerate)
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Calls generateNewTermSheet, then resolves currentTermSheetVersion from the
   * response.  Autogenerate always creates (never updates).
   */
  const createTermSheet = useCallback(
    async (
      loanData: any,
      defaultFees: any,
      tempTermSheetData: any,
      suggestedTerms: any,
    ) => {
      try {
        const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
        const payload = buildAutoGeneratePayload(
          loanData,
          defaultFees,
          tempTermSheetData,
          suggestedTerms,
        );
        const { data } = await generateNewTermSheet(
          payload,
          loanId,
          realmHeaders,
        );
        setCurrentTermSheetVersion(data?.data?.termSheetVersion);
      } catch (err) {
        console.error(err);
        throw err; // re-throw so saveOrUpdateNewTermSheet can catch it
      }
    },
    [signatureData, dynamicConfig, loanId],
  );

  // ══════════════════════════════════════════════════════════════════════════
  // 8. DOWNLOAD PDF
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Renders the term-sheet as a PDF.
   * Returns the hosted URL (used as email attachment).
   * `download = false` skips triggering browser download.
   */
  const downloadTermsheetHandler = async (
    data: any,
    download: boolean = true,
  ) => {
    const headers = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
    const payload: any = buildEmailPayload(data);
    payload.templateData.templateName = "los_generic_term_sheet.ftl";
    payload.templateData.fileName = `TermSheet_${evaluationID}_${formatDateForPDFName(new Date())}`;
    payload.templateData.originatorLogoBgColor = "";

    const pdf = await downloadTermsheetPdf(
      { ...payload?.templateData },
      headers,
    );
    if (download) {
      const link = document.createElement("a");
      link.href = pdf.data;
      link.target = "_blank";
      setLatestPDF_URL(pdf.data);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      return pdf.data;
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 9. SEND EMAIL  (autogenerate always sends after saving)
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Generates the PDF (if not already done) then dispatches the email.
   * For autogenerate, toEmails may be empty – the email is still "sent"
   * internally to mark emailSentOn.
   */
  const sendAutoGenerateEmail = async (tempTermSheetData: any) => {
    const headers = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
    const payload: any = buildEmailPayload(tempTermSheetData);
    payload.attachment = [
      {
        path: latestPDF_URL,
        filename: `TermSheet_${evaluationID}_${formatDateForPDFName(new Date())}.pdf`,
      },
    ];
    payload.templateData.smtpIp = originatorsAccountMappingDetails.smtpIp;
    payload.templateData.passKey = originatorsAccountMappingDetails.smtpIp;

    // If no PDF URL yet, render it first
    if (!latestPDF_URL) {
      const url = await downloadTermsheetHandler(tempTermSheetData, false);
      payload.attachment = [
        {
          path: url,
          filename: `TermSheet_${evaluationID}_${formatDateForPDFName(new Date())}.pdf`,
        },
      ];
      setLatestPDF_URL(url);
    }

    await sendTermSheetEmail(payload, headers, dynamicConfig);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 10. SAVE + EMAIL  (always "create" + isEmailSent=true for autogenerate)
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Orchestrates:
   *   1. Derive structured loan / suggested data from loanContextData.
   *   2. Set emailSentOn timestamp.
   *   3. createTermSheet()  →  POST to backend.
   *   4. sendAutoGenerateEmail()  →  render PDF + send email.
   *   5. Clean up context state.
   */
  const saveAndEmailAutoGeneratedTermSheet = async (tempTermSheetData: any) => {
    try {
      const loanTypeStr =
        loanContextData?.loanDetails?.productType ||
        loanContextData?.fetchData?.productType;

      const data: any = getTermSheetData(
        loanTypeStr,
        loanContextData,
        t,
        "autogenerate", // termSheetAction fixed for this hook
        tempTermSheetData,
        loanContextData?.defaultSelectedDataView,
        evaluationID,
        loanId,
        waiverRequest,
      );

      const suggestedTerms = getSuggestedLoanTerms(
        loanTypeStr,
        loanContextData,
        t,
        "autogenerate",
        tempTermSheetData,
      );

      // Always stamp emailSentOn for autogenerate
      data.emailSentOn = moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

      await createTermSheet(
        data,
        tempTermSheetData?.feeDetails,
        tempTermSheetData,
        suggestedTerms,
      );
      await sendAutoGenerateEmail(tempTermSheetData);

      // Tear-down
      setIsOpenTermSheet(false);
      setTermSheetAction("");
      setSnackBarMessage("Term Sheet Generated And Saved Successfully.");
      return { success: true };
    } catch (error) {
      console.error(error);
      setIsOpenTermSheet(false);
      setTermSheetAction("");
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 11.  MULTI-PROPERTY HELPERS
  // ══════════════════════════════════════════════════════════════════════════

  /** Watch property count and toggle isMultiProperty. */
  useEffect(() => {
    const len =
      loanContextData?.propertyDetails?.length ||
      loanContextData?.loanDetails?.propertyDetails?.length ||
      0;
    setIsMultiProperty(len > 1);
  }, [loanContextData?.propertyDetails, loanContextData?.loanDetails]);

  /**
   * When dealing with multiple properties:
   *   - Builds propertyAddressDetails array from each property.
   *   - Strips the generic "Property Address" key from loanData rows.
   */
  const applyMultiPropertyTransform = (data: any[], loanCtx: any) => {
    const propertyDetails = loanCtx?.propertyDetails?.length
      ? loanCtx?.propertyDetails
      : loanCtx?.loanDetails?.propertyDetails;

    const propertyAddressDetails = propertyDetails?.map(
      (prop: any, index: number) => ({
        label: `Property ${index + 1}`,
        value: cleanedAddressString(prop.propertyLocation),
      }),
    );

    // Remove the single-property "Property Address" field from each row
    data.forEach((subArray: any) => {
      subArray.forEach((item: any) => {
        if (Object.prototype.hasOwnProperty.call(item, "Property Address")) {
          delete item["Property Address"];
        }
      });
    });

    return propertyAddressDetails;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 12.  MAIN ENTRY POINT  –  getTermSheetDataBasedOnAction (autogenerate only)
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Mirrors the "autogenerate" branch of the original
   * getTermSheetDataBasedOnAction function.
   *
   * Two sub-paths:
   *   A) No existing term-sheet  (latestTermSheetVersion is falsy)
   *      → Build from scratch using loanContextData + org fee defaults.
   *
   *   B) Existing term-sheet found  (latestTermSheetVersion is truthy)
   *      → Fetch that version, overlay fresh loan data, mark isAutoGenerated=true.
   *
   * Both paths end with saveAndEmailAutoGeneratedTermSheet().
   */
  const getTermSheetDataBasedOnAction = async (action: string) => {
    // Guard: only run for autogenerate
    if (![termSheetAction, action].includes("autogenerate")) return;

    // ── Always refresh waivers first ────────────────────────────────────────
    const newWaiverRequest = await getWaivers();

    const fromEmail =
      originatorsAccountMappingDetails?.fromEmail ?? "noreply@bplsizer.com";

    const loanTypeStr =
      loanContextData?.loanDetails?.productType ||
      loanContextData?.fetchData?.productType;

    // Shared variables populated by either branch
    let data: any = [];
    let newSuggestedLoanTerms: any = [];
    let newRequirements: string[] = [""];
    let newFeeDetails: any = {};
    let propertyAddressDetails: any = null;
    let toEmails: any = [];
    let ccEmails: any = [];
    let emailBody: any = null;
    let subject: string | null = null;
    let termSheetName: string = getTermsheetDateTimeStampName();
    let currentVersion: string = `${(+latestTermSheetVersion || 0) + 1}`;

    // ── PATH A: No existing version → generate from scratch ────────────────
    if (!latestTermSheetVersion) {
      data = getTermSheetData(
        loanTypeStr,
        loanContextData,
        t,
        "autogenerate",
        termSheetData,
        loanContextData?.defaultSelectedDataView,
        evaluationID,
        loanId,
        newWaiverRequest,
      );
      newSuggestedLoanTerms = getSuggestedLoanTerms(
        loanTypeStr,
        loanContextData,
        t,
        termSheetData,
      );
      newFeeDetails = getFeeDetails();
      newRequirements = [...orgLevelRequirements];
      emailBody = emailBodyStr2;

      if (isMultiProperty) {
        propertyAddressDetails = applyMultiPropertyTransform(
          data,
          loanContextData,
        );
      }

      // ── PATH B: Existing version → fetch it, then overlay autogenerate data ─
    } else {
      const response: any = await fetchGetTermSheetBasedOnVersion(
        currentTermSheetVersion,
      );

      if (response) {
        data = getTermSheetData(
          loanTypeStr,
          loanContextData,
          t,
          "autogenerate",
          response,
          loanContextData?.defaultSelectedDataView,
          evaluationID,
          loanId,
          newWaiverRequest,
        );
        newSuggestedLoanTerms = getSuggestedLoanTerms(
          loanTypeStr,
          loanContextData,
          t,
          "autogenerate",
          response,
        );
        newRequirements = response?.requirements || [""];
        newFeeDetails =
          response?.feeDetails ??
          JSON.parse(JSON.stringify(termSheetData?.feeDetails ?? "{}"));
        ccEmails = response?.email?.cc;
        toEmails = response?.email?.to;
        emailBody = response?.email?.content;
        subject = response?.email?.subject;
        currentVersion = response?.termSheetVersion;
        termSheetName = getTermsheetDateTimeStampName(); // always fresh name

        // Parse propertyAddressDetails if present
        propertyAddressDetails =
          response?.propertyAddressDetails &&
          Object.keys(response?.propertyAddressDetails)?.map((key: any) => ({
            label: key,
            value: response?.propertyAddressDetails[key],
          }));

        // Multi-property: rebuild address list from live context
        if (isMultiProperty) {
          propertyAddressDetails = applyMultiPropertyTransform(
            data,
            loanContextData,
          );
        }

        // Restore signature from saved version
        setSignatureData(
          response?.signatureInfo ?? {
            name: decodedData?.name,
            email: decodedData?.email,
            accountName: originatorData?.accountName,
            phone: decodedData?.phone,
          },
        );
      }
    }

    // ── Sync context stores ─────────────────────────────────────────────────
    setTermSheetEmailData({ fromEmail, toEmails, ccEmails });
    setTermSheetLoanData(data);
    setTermSheetSuggestedLoanData(newSuggestedLoanTerms);
    setTermSheetFeeData(newFeeDetails);
    setTermSheetRequirementsData(newRequirements || [""]);

    // Patch orgLevelFees visibility based on what is present in newFeeDetails
    const updatedOrgLevelFees = orgLevelFees?.map((item: any) => {
      const feeExists = Object.prototype.hasOwnProperty.call(
        newFeeDetails,
        item.label,
      );
      return {
        ...item,
        isVisible: () => (feeExists ? true : item?.isVisible()),
      };
    });
    setOrgLevelFees(updatedOrgLevelFees);

    // ── Build final term-sheet data object ───────────────────────────────────
    const newTermSheetData = {
      emailSentOn: null, // will be set in saveAndEmail
      loanData: data,
      suggestedLoanTerms: newSuggestedLoanTerms,
      requirements: newRequirements || [""],
      feeDetails: newFeeDetails,
      emailBody: emailBody ?? emailBodyStr2,
      emails: {
        fromEmail,
        toEmails,
        ccEmails,
        subject,
        smtpIp: originatorsAccountMappingDetails.smtpIp,
        passKey: originatorsAccountMappingDetails.smtpIp,
      },
      date: moment(new Date()).format("MM/DD/YYYY"),
      currentTermSheetVersion: currentVersion,
      propertyAddressDetails,
      evaluationID,
      termSheetName,
      isAutoGenerated: true, // always true
    };

    setTermSheetData(newTermSheetData);

    // ── Autogenerate always saves + emails immediately ───────────────────────
    return await saveAndEmailAutoGeneratedTermSheet(newTermSheetData);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 13.  TRIGGER EFFECT
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * Mirrors the original effect that fires getTermSheetDataBasedOnAction.
   * Condition retained from the original:
   *   termSheetAction === "autogenerate"  is enough to trigger this hook.
   */
  useEffect(() => {
    if (termSheetAction === "autogenerate") {
      getTermSheetDataBasedOnAction(termSheetAction);
    }
  }, [termSheetAction, defaultFeeDetails]);

  // ══════════════════════════════════════════════════════════════════════════
  // 14.  INITIALISATION ON MOUNT
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    setFeeDetailsConfig();
    getWaivers();
  }, []);

  // Public surface (nothing needs to be consumed externally for autogenerate –
  // it is fully self-contained, but expose the trigger for test/manual use).
  return { getTermSheetDataBasedOnAction };
}
